import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useUser } from "@clerk/clerk-react";
import { getUserProgress, updateLessonProgress } from "../data/database";
import SaveButton from "../components/SaveButton";

const animals = [
  { word: "قطة", pronunciation: "Qiṭṭa", meaning: "Cat", emoji: "🐱" },
  { word: "كلب", pronunciation: "Kalb", meaning: "Dog", emoji: "🐶" },
  { word: "حصان", pronunciation: "Hisaan", meaning: "Horse", emoji: "🐴" },
  { word: "بقرة", pronunciation: "Baqara", meaning: "Cow", emoji: "🐮" },
  { word: "خروف", pronunciation: "Kharuf", meaning: "Sheep", emoji: "🐑" },
  { word: "جمل", pronunciation: "Jamal", meaning: "Camel", emoji: "🐪" },
  { word: "أسد", pronunciation: "Asad", meaning: "Lion", emoji: "🦁" },
  { word: "فيل", pronunciation: "Feel", meaning: "Elephant", emoji: "🐘" },
  { word: "دجاجة", pronunciation: "Dajajah", meaning: "Chicken", emoji: "🐔" },
  { word: "سمكة", pronunciation: "Samakah", meaning: "Fish", emoji: "🐟" }
];

function AnimalsLesson() {
  const { user } = useUser();
  const [selectedAnimal, setSelectedAnimal] = useState(null);
  const [clickedAnimals, setClickedAnimals] = useState(new Set());

  // Load progress from database on mount
  useEffect(() => {
    const loadProgress = () => {
      if (user) {
        const userProgress = getUserProgress(user.id);
        const learnedCount = userProgress.animals || 0;
        if (learnedCount > 0) {
          setClickedAnimals(new Set(animals.slice(0, learnedCount).map(a => a.word)));
        }
      }
    };
    loadProgress();
  }, [user]);

  const handleClick = async (animal) => {
    setSelectedAnimal(animal);
    const updated = new Set(clickedAnimals).add(animal.word);
    setClickedAnimals(updated);
    
    // Save progress to database
    if (user) {
      try {
        await updateLessonProgress(user.id, 'animals', updated.size);
        console.log('Progress saved to database:', updated.size);
      } catch (error) {
        console.error('Error saving progress to database:', error);
      }
    }
    window.responsiveVoice.speak(animal.word, "Arabic Male");
  };

  const progress = (clickedAnimals.size / animals.length) * 100;

  return (
    <div className="p-4 sm:p-6 text-center h-full bg-gradient-to-br from-green-50 to-white overflow-y-auto">
      <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-green-800">الحيوانات</h2>

      {/* Progress Bar */}
      <div className="w-full max-w-md mx-auto bg-gray-200 rounded-full h-4 mb-4 overflow-hidden shadow-inner">
        <motion.div
          className="bg-green-500 h-full"
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.5 }}
        />
      </div>
      <p className="text-sm text-gray-600 mb-6">تم تعلم {clickedAnimals.size} من {animals.length} حيوان</p>

      <AnimatePresence>
        {selectedAnimal && (
          <motion.div
            key="animal-info"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.3 }}
            className="text-lg sm:text-xl text-green-700 mb-6"
          >
            <p>Pronunciation: <strong>{selectedAnimal.pronunciation}</strong></p>
            <p>Meaning: <strong>{selectedAnimal.meaning}</strong></p>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 justify-center mb-6">
        {animals.map((animal, index) => {
          const learned = clickedAnimals.has(animal.word);
          return (
            <button
              key={index}
              onClick={() => handleClick(animal)}
              className={`text-xl font-semibold p-4 rounded-2xl shadow-md transition duration-200 text-wrap whitespace-pre-wrap break-words ${
                learned ? "bg-green-100 text-green-700" : "bg-white text-yellow-800 hover:bg-yellow-100"
              }`}
            >
              <div className="text-3xl">{animal.emoji}</div>
              {animal.word}
            </button>
          );
        })}
      </div>

      {/* Save Button */}
      <div className="flex justify-center mb-6">
        <SaveButton
          lessonType="animals"
          progressData={clickedAnimals.size}
        />
      </div>
    </div>
  );
}

export default AnimalsLesson;
